/**
 * ViewMode context — controls which audience the report tree is
 * rendering for. Four levels:
 *
 *   - 'editor' (default): the report's creator is looking at it inside
 *     the product. Admin signals are visible: the "(overridden for
 *     this report)" chip, the edit-history appendix, the edit pencils,
 *     the Quality / Sanity / Generation Cost panels above the report,
 *     etc.
 *
 *   - 'reader': a logged-in collaborator is reading the report. Same
 *     as 'editor' for now (no shareable-view code path identified
 *     yet); reserved so the audience hierarchy is explicit and the
 *     reader-only suppression policy can be added in one place
 *     when shareable views land.
 *
 *   - 'export': the report is being rendered into a PDF popup window
 *     or a DOCX file. Admin signals are stripped. This is the
 *     audience the customer actually receives, so leaking internal
 *     pipeline state here is the bug class this context exists to
 *     prevent.
 *
 *   - 'public-share': an un-authenticated reader is viewing the
 *     report via a public /share/:id URL (SharedReportView). Same
 *     suppression policy as 'export' for admin-only output, but
 *     leaf components are encouraged to render a small VISIBLE
 *     placeholder where an admin-gated block would have been, so
 *     the reader has a signal that something internal was hidden
 *     rather than silently blank space. PH pack, PH-C3.
 *
 * Usage pattern:
 *   - Wrap the report tree in <ViewModeProvider value={...}>.
 *   - Subtrees that emit admin-only output call `useViewMode()` and
 *     gate on the result. Default-on behavior (no provider in the
 *     tree) is `'editor'` so legacy callers stay correct.
 *
 * Why a context: the override chip sits a dozen levels deep inside
 * SiteRagReportView, and the edit-history appendix lives in
 * ReferencesSection. Threading a prop through every intermediate
 * component would be miserable; a single context provider at the
 * report root reaches both of them in one shot.
 */
import React from 'react';

export type ViewMode = 'editor' | 'reader' | 'export' | 'public-share';

const ViewModeContext = React.createContext<ViewMode>('editor');

export interface ViewModeProviderProps {
  value: ViewMode;
  children: React.ReactNode;
}

export function ViewModeProvider({ value, children }: ViewModeProviderProps): React.ReactElement {
  return <ViewModeContext.Provider value={value}>{children}</ViewModeContext.Provider>;
}

export function useViewMode(): ViewMode {
  return React.useContext(ViewModeContext);
}

/** True when the rendered output is destined for the customer (PDF / DOCX / shared view). */
export function isExportMode(mode: ViewMode): boolean {
  return mode === 'export';
}

/**
 * True when the rendered output is reaching an un-authenticated
 * reader via a public /share/:id URL. PH-C3 — components that admin-gate
 * internal telemetry (e.g., the Google API debug appendix) can use
 * this to render a small visible "Admin-only block hidden" placeholder
 * instead of silently disappearing, so the reader has a signal that
 * a block was suppressed rather than an unexplained gap.
 */
export function isPublicShareMode(mode: ViewMode): boolean {
  return mode === 'public-share';
}
